import React, { Component } from "react";
import { connect } from "react-redux";
import withStyles from "@material-ui/core/styles/withStyles";
import CompetitionActions from "../../actions/competitions";
import { GlobalState } from "../../types/globalState";
import BorderedBox from "../SharedComponents/BorderedBox";
import {
  Button,
  createStyles,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Switch,
  Theme,
  Typography,
  WithStyles,
  FormControlLabel,
} from "@material-ui/core";
import { showSnackbar } from "../SharedComponents/Notifier";
import {
  Competition,
  PlayingRulesType,
  ExpireRules,
  TeamHandicapAllowance,
  PlayingFormats,
  PlayingAsFormats,
  CompetitionInput,
  HandicapRules as HandicapRulesType,
  Eligibility,
  GameRules,
  RuleType,
} from "../../types/competition";
import LinearProgress from "@material-ui/core/LinearProgress";
import { hideDialog } from "../SharedComponents/Dialog";
import Selector, { Option } from "../SharedComponents/Selector";
import Config from "../../config";
import PlayingRules from "./PlayingRules";
import HandicapRules from "./HandicapRules";
import { TextInput } from "../SharedComponents/TextInput";
import { filter } from "../../utils/profanityFilter";
import { HoleNumbers } from "../../types/course";
import { Rules, User, UserBrifObj } from "../../types/user";
import { GameAdditionalOrganisersTable as OrganisersTable } from "components/Game/GameAdditionalOrganiserTable";
import HandicapsExpireRules from "./ExpireRules";
import HandicapAllowance from "./HandicapAllowance";
import HandicapAdjustmentSection from "./HandicapAdjustment";
import HolesNumberPicker from "./HolesNumberPicker";
import CompetitionEligability from "./CompetitionEligability";
import CompetitionApi from "../../api/competitions";

const accessorKey = (list: UserBrifObj[] | undefined) =>
  (list || [])
    .map(a => (a?._id != null ? String(a._id) : ""))
    .filter(Boolean)
    .sort()
    .join(",");

const COMPETITIONS_FETCH_LIMIT = 2000;

interface Props extends WithStyles {
  insertCompetition(data: CompetitionInput): void;
  updateCompetition(id: string, data: CompetitionInput): void;
  fetchCompetitionsList(
    page: number,
    limit: number,
    filter?: string,
    sort?: string,
    dir?: string,
    query?: string,
  ): void;
  loading: boolean;
  user: User;
  competitions: Competition[];
  edit?: Competition;
  copy?: Competition;
}

interface State {
  competition: CompetitionInput;
  err_name?: boolean;
  err_eligibility?: boolean;
  err_intro?: boolean;
  err_expire?: boolean;
  err_socialHandicap?: boolean;
  err_whsHandicap?: boolean;
  loading?: boolean;
  shareApplyLoading?: boolean;
  shareError?: string | null;
  /** Selected rule owner (custom rule, saved) — set only with row switches, then Apply or Save. */
  pendingOwnerId: string;
  serverOwnerId: string;
  serverAccessorKey: string;
}

class CompetitionSetup extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const playingRules: PlayingRulesType = {
      playingFormat: PlayingFormats.Individual,
      playingAs: PlayingAsFormats.Individual,
      teamPlayers: 2,
      teamsPerTee: 1,
      bestOne: [],
      bestTwo: [],
      bestThree: [],
      bestFour: [],
      isRelatedToCourseHoles: true,
      minimumTeeShots: 0,
      multiRules: false,
    };
    const handicapRules: HandicapRulesType = {
      adjustHandicapOnResult: false,
      adjustHandicapOnPoints: false,
      adjustHandicapOnAverageScore: false,
      customHandicapCategories: false,
      aboveHIadjustment: true,
      disregardDnp: false,
      sharedPrize: false,
      exactHandicaps: false,
      usingNumberInFirstTimeHandicapAdjustment: true,
      firstTimeHandicapAdjustment: 0,
      handicapDeducting: [],
      losersHandicapDeducting: [],
      handicapAdjustmentRules: [],
      allowPlayerWithSocialHandicap: false,
      socialHandicapAsHandicapIndex: false,
      socialHandicapAsCourseHandicap: false,
      socialHandicapAsPlayingHandicap: false,
      useSocialHcpIfWhsHiMissing: false,
      applySlopeForSocialHcp: false,
      removeSlopeCalculation: false,
      removeCourseRatingMinusPar: false,
      whsFirstGameAsHandicapIndex: true,
      whsFirstGameAsCourseHandicap: false,
      whsFirstGameAsPlayingHandicap: false,
    };
    const teamHandicapAllowance: TeamHandicapAllowance = {
      hcpSortedAllowances: [100, 100, 100, 100],
      combinedTeam: 100,
    };
    let competition: CompetitionInput = {
      type: this._getDefaultRuleType(),
      accessor: [],
      name: "",
      introduction: "",
      scoringFormat: "stableford",
      individual: 4,
      eligibility: [
        {
          exist: false,
          gender: "men",
          maxHandicap: 54,
          minHandicap: 0,
        },
        {
          exist: false,
          gender: "women",
          maxHandicap: 54,
          minHandicap: 0,
        },
      ],
      playingRules: playingRules,
      handicapRules: handicapRules,
      teamHandicapAllowance: teamHandicapAllowance,
      fullHandicapAllowance: 100,
      guests: false,
      expireRules: { isActive: true, minGames: 0, duringMonths: 0, minMonths: 0 },
      holesNumber: 18,
      active: true,
    };
    if (props.edit) competition = this._copyFields(props.edit, playingRules, handicapRules, teamHandicapAllowance);
    else if (props.copy) competition = this._copyFields(props.copy, playingRules, handicapRules, teamHandicapAllowance);

    const e = props.edit;
    const uid = String(props.user._id);
    let pendingOwnerId = uid;
    let serverOwnerId = "";
    let serverAccessorKey = "";
    if (e?._id && e.owner) {
      const own = (e.owner as UserBrifObj)._id != null ? String((e.owner as UserBrifObj)._id) : String((e as { owner?: string }).owner);
      pendingOwnerId = own;
      serverOwnerId = own;
    }
    if (e?._id) {
      serverAccessorKey = accessorKey((e.accessor || []) as UserBrifObj[]);
    }
    this.state = {
      competition,
      loading: false,
      shareApplyLoading: false,
      shareError: null,
      err_intro: false,
      err_name: false,
      err_expire: false,
      err_eligibility: false,
      pendingOwnerId,
      serverOwnerId,
      serverAccessorKey,
    };
  }

  _copyFields = (cmp: Competition, pr: PlayingRulesType, hr: HandicapRulesType, th: TeamHandicapAllowance) => {
    const { _id, accessor: _ac, ...competition } = cmp;

    let acc = _ac || [];
    if (cmp.owner && typeof cmp.owner === "object" && (cmp.owner as UserBrifObj)._id) {
      const oid = String((cmp.owner as UserBrifObj)._id);
      if (oid && !acc.some(a => a && String(a._id) === oid)) {
        acc = [cmp.owner as UserBrifObj, ...acc];
      }
    }

    competition.type = this._getDefaultRuleType(competition.type);

    if (!competition.playingRules) competition.playingRules = pr;

    if (!competition.handicapRules) {
      competition.handicapRules = {
        ...hr,
        allowPlayerWithSocialHandicap: cmp.handicapRules?.allowPlayerWithSocialHandicap ?? false,
        socialHandicapAsHandicapIndex: cmp.handicapRules?.socialHandicapAsHandicapIndex ?? false,
        socialHandicapAsCourseHandicap: cmp.handicapRules?.socialHandicapAsCourseHandicap ?? false,
        socialHandicapAsPlayingHandicap: cmp.handicapRules?.socialHandicapAsPlayingHandicap ?? false,
      };
    }

    if (!competition.teamHandicapAllowance) competition.teamHandicapAllowance = th;

    const hrules = competition.handicapRules;
    if (hrules) {
      const whsHi = hrules.whsFirstGameAsHandicapIndex === true;
      const whsCh = hrules.whsFirstGameAsCourseHandicap === true;
      const whsPh = hrules.whsFirstGameAsPlayingHandicap === true;
      if (!whsHi && !whsCh && !whsPh) {
        competition.handicapRules = {
          ...hrules,
          whsFirstGameAsHandicapIndex: true,
          whsFirstGameAsCourseHandicap: false,
          whsFirstGameAsPlayingHandicap: false,
        };
      }
    }

    return { ...competition, accessor: acc };
  };

  _getDefaultRuleType = (type?: GameRules) => {
    return this.props.user.rule !== Rules.Admin ? GameRules.CUSTOM : type || GameRules.BASIC;
  };

  componentDidMount() {
    this.props.fetchCompetitionsList(1, COMPETITIONS_FETCH_LIMIT);
  }

  formValidation = () => {
    const { name, introduction, type, expireRules, handicapRules } = this.state.competition;

    const err_name = name.trim().length < 2 || filter.isProfane(name);
    const err_intro = filter.isProfane(introduction);
    const eligibility = this.state.competition.eligibility;
    const err_eligibility = !eligibility[0].exist && !eligibility[1].exist;
    const err_hcpAbove = type === GameRules.CUSTOM && (!handicapRules || handicapRules.aboveHIadjustment === undefined);
    const err_expire =
      type === GameRules.CUSTOM &&
      expireRules?.isActive &&
      (!expireRules?.minMonths || !expireRules?.minGames || !expireRules?.duringMonths);

    let err_socialHandicap = false;
    if (handicapRules.allowPlayerWithSocialHandicap) {
      const selected =
        [handicapRules.socialHandicapAsHandicapIndex, handicapRules.socialHandicapAsCourseHandicap, handicapRules.socialHandicapAsPlayingHandicap]
          .filter(Boolean).length;
      if (selected !== 1) {
        err_socialHandicap = true;
        showSnackbar("Please select exactly one option for social handicap usage.");
      }
    }

    let err_whsHandicap = false;
    const whsSelected = [
      handicapRules.whsFirstGameAsHandicapIndex,
      handicapRules.whsFirstGameAsCourseHandicap,
      handicapRules.whsFirstGameAsPlayingHandicap,
    ].filter(Boolean).length;
    if (whsSelected !== 1) {
      err_whsHandicap = true;
      showSnackbar("Please select exactly one WHS handicap option.");
    }

    this.setState({
      err_name,
      err_intro,
      err_eligibility,
      err_expire,
      err_socialHandicap,
      err_whsHandicap,
    });

    if (err_hcpAbove) showSnackbar("Please determine if players can go above their PH.");

    return !(
      err_name ||
      err_intro ||
      err_eligibility ||
      err_expire ||
      err_hcpAbove ||
      err_socialHandicap ||
      err_whsHandicap
    );
  };

  handleTextInputChange = (value: string, name: string) => {
    this.setState({
      competition: { ...this.state.competition, [name]: value },
    });
  };

  handleGuestSwitchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      competition: {
        ...this.state.competition,
        guests: event.target.checked as boolean,
      },
    });
  };

  handleEligibilityChanged = (eligibility: Eligibility[]) => {
    this.setState({
      competition: { ...this.state.competition, eligibility },
    });
  };

  _isShareSectionDirty = () => {
    const { edit } = this.props;
    if (!edit?._id) return false;
    const { competition, pendingOwnerId, serverOwnerId, serverAccessorKey } = this.state;
    if (String(pendingOwnerId) !== String(serverOwnerId)) return true;
    return accessorKey(competition.accessor) !== serverAccessorKey;
  };

  _handlePendingOwnerIdChange = (id: string) => {
    this.setState({ pendingOwnerId: id, shareError: null });
  };

  _handleAccessorChange = (organisers: UserBrifObj[]) => {
    let { pendingOwnerId } = this.state;
    if (pendingOwnerId && !organisers.some(o => o && String(o._id) === String(pendingOwnerId))) {
      const uid = String(this.props.user._id);
      const urow = organisers.find(o => o && String(o._id) === uid);
      if (urow) pendingOwnerId = uid;
      else if (organisers[0]) pendingOwnerId = String(organisers[0]._id);
    }
    this.setState({
      competition: { ...this.state.competition, accessor: organisers },
      pendingOwnerId,
      shareError: null,
    });
  };

  _applySharingAndOwnership = async (fromSave: boolean) => {
    const { edit } = this.props;
    const { competition, pendingOwnerId, serverOwnerId, shareApplyLoading } = this.state;
    if (shareApplyLoading) return false;
    if (!edit?._id) {
      if (!fromSave) showSnackbar("Save the rule first, then you can update sharing and ownership.");
      return false;
    }
    if (competition.type !== GameRules.CUSTOM) return true;
    if (!this._isShareSectionDirty()) return true;
    this.setState({ shareApplyLoading: true, shareError: null });
    const ownerId = String(pendingOwnerId);
    if (!(competition.accessor || []).some(a => a && String(a._id) === ownerId)) {
      const msg = "The owner must be in the list. Add that person as an organiser, or set another owner with the switch.";
      this.setState({ shareApplyLoading: false, shareError: msg });
      showSnackbar(msg);
      return false;
    }
    const {
      competition: { owner, active, ...cmp },
    } = this.state;
    const updatePayload = this._validateTeamRules(cmp);
    try {
      await CompetitionApi.update(edit._id, updatePayload);
      if (String(ownerId) !== String(serverOwnerId)) {
        await CompetitionApi.transferOwner(edit._id, ownerId);
      }
      this.setState({
        shareApplyLoading: false,
        shareError: null,
        serverOwnerId: ownerId,
        serverAccessorKey: accessorKey(competition.accessor),
      });
      if (!fromSave) {
        showSnackbar("Sharing and ownership applied");
      }
      return true;
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        (err?.message === "Network Error" ? "API is unreachable. Ensure the backend is running on the configured API URL." : null) ||
        err?.message ||
        "Update failed";
      const pretty = typeof msg === "string" ? msg : "Update failed";
      this.setState({ shareApplyLoading: false, shareError: pretty });
      showSnackbar(pretty);
      return false;
    }
  };

  _onApplySharingClick = () => {
    this._applySharingAndOwnership(false);
  };

  _onScoringFormatChanges = (event: React.ChangeEvent<{ value: unknown }>) => {
    const scoringFormat = event.target.value as string;
    const handicapRules = { ...this.state.competition.handicapRules };
    if (scoringFormat !== Config.SCORING_FORMAT.STABLEFORD) {
      handicapRules.adjustHandicapOnAverageScore = false;
    }
    this.setState({
      competition: {
        ...this.state.competition,
        scoringFormat,
        handicapRules,
      },
    });
  };

  _onIndividualChanges = (event: React.ChangeEvent<{ value: unknown }>) => {
    this.setState({
      competition: {
        ...this.state.competition,
        individual: event.target.value as number,
      },
    });
  };

  handleHandicapAllowanceChanged = (value: {
    fullHandicapAllowance: number;
    teamHandicapAllowance: TeamHandicapAllowance;
  }) => {
    this.setState({
      competition: {
        ...this.state.competition,
        fullHandicapAllowance: value.fullHandicapAllowance,
        teamHandicapAllowance: value.teamHandicapAllowance,
      },
    });
  };

  ruleTypes: Option[] = [
    { label: "Standard (basic)", value: GameRules.BASIC },
    { label: "Standard (WHS)", value: GameRules.WHS },
    { label: "Custom", value: GameRules.CUSTOM },
  ];

  _handleRuleTypeChange = (type: RuleType) => {
    this.setState({
      competition: { ...this.state.competition, type },
    });
  };

  onChangeHoleNumbers = (value: string | number) => {
    this.setState({
      competition: {
        ...this.state.competition,
        holesNumber: value as HoleNumbers,
      },
    });
  };

  handleHandicapAdjustmentSelectChanges = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    if (!event.target.name) return;
    this.setState({
      competition: {
        ...this.state.competition,
        handicapAdjustment: {
          ...this.state.competition.handicapAdjustment,
          [event.target.name]: event.target.value === 1,
        },
      },
    });
  };

  handleExpireRulesSwitchChanged = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const expireRules = {
      ...this.state.competition.expireRules,
      [field]: event.target.checked as boolean,
    } as ExpireRules;
    this.setState({
      competition: {
        ...this.state.competition,
        expireRules,
      },
    });
  };

  handleHandicapExpireRulesChanged = (expireRules: ExpireRules) => {
    this.setState({
      competition: {
        ...this.state.competition,
        expireRules,
      },
    });
  };

  handleHandicapRulesChanged = (handicapRules: HandicapRulesType) => {
    this.setState({
      competition: {
        ...this.state.competition,
        handicapRules,
      },
    });
  };

  handlePlayingRulesChanged = (playingRules: PlayingRulesType) => {
    this.setState({
      competition: {
        ...this.state.competition,
        playingRules,
      },
    });
  };

  _validateTeamRules = (cmp: CompetitionInput) => {
    const newCompetition = {
      ...cmp,
      name: cmp.name.trim(),
      introduction: cmp.introduction.trim(),
    };
    delete newCompetition.handicapSourceType;
    delete newCompetition.handicapSourceCompetitionId;
    delete newCompetition.handicapAdjustmentMode;
    if (!cmp.playingRules.multiRules) return newCompetition;
    if (!Array.isArray(cmp.playingRules.minimumTeeShots)) {
      const value = cmp.playingRules.minimumTeeShots || 0;
      newCompetition.playingRules.minimumTeeShots = Array.from({ length: cmp.playingRules.teamPlayers - 1 }).fill(
        value,
      ) as number[];
    }
    if (!Array.isArray(cmp.teamHandicapAllowance.combinedTeam)) {
      const value = cmp.teamHandicapAllowance.combinedTeam || 100;
      newCompetition.teamHandicapAllowance.combinedTeam = Array.from({ length: cmp.playingRules.teamPlayers - 1 }).fill(
        value,
      ) as number[];
    }
    return newCompetition;
  };

  _submitForm = async () => {
    const {
      competition: { owner, ...cmp },
    } = this.state;
    const { edit } = this.props;
    const newCompetition = this._validateTeamRules(cmp);
    if (!this.formValidation()) {
      showSnackbar("Form is not valid, check items");
      return;
    }
    if (edit && this.state.competition.type === GameRules.CUSTOM) {
      const ok = await this._applySharingAndOwnership(true);
      if (!ok) return;
    }
    if (edit) {
      this.props.updateCompetition(edit._id, newCompetition);
    } else {
      this.props.insertCompetition(newCompetition);
    }
  };

  // --- Social Handicap Handlers ---
  _handleAllowPlayerWithSocialHandicapChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    this.setState({
      competition: {
        ...this.state.competition,
        handicapRules: {
          ...this.state.competition.handicapRules,
          allowPlayerWithSocialHandicap: checked,
          socialHandicapAsHandicapIndex: checked
            ? this.state.competition.handicapRules.socialHandicapAsHandicapIndex
            : false,
          socialHandicapAsCourseHandicap: checked
            ? this.state.competition.handicapRules.socialHandicapAsCourseHandicap
            : false,
          socialHandicapAsPlayingHandicap: checked
            ? this.state.competition.handicapRules.socialHandicapAsPlayingHandicap
            : false,
        },
      },
    });
  };

  _handleSocialHandicapAsHIChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    this.setState((prevState) => ({
      competition: {
        ...prevState.competition,
        handicapRules: {
          ...prevState.competition.handicapRules,
          socialHandicapAsHandicapIndex: checked,
          socialHandicapAsCourseHandicap: checked ? false : prevState.competition.handicapRules.socialHandicapAsCourseHandicap,
          socialHandicapAsPlayingHandicap: checked ? false : prevState.competition.handicapRules.socialHandicapAsPlayingHandicap,
        },
      },
      err_socialHandicap: false,
    }));
  };

  _handleSocialHandicapAsCHChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    this.setState((prevState) => ({
      competition: {
        ...prevState.competition,
        handicapRules: {
          ...prevState.competition.handicapRules,
          socialHandicapAsHandicapIndex: checked ? false : prevState.competition.handicapRules.socialHandicapAsHandicapIndex,
          socialHandicapAsCourseHandicap: checked,
          socialHandicapAsPlayingHandicap: checked ? false : prevState.competition.handicapRules.socialHandicapAsPlayingHandicap,
        },
      },
      err_socialHandicap: false,
    }));
  };

  _handleSocialHandicapAsPHChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    this.setState((prevState) => ({
      competition: {
        ...prevState.competition,
        handicapRules: {
          ...prevState.competition.handicapRules,
          socialHandicapAsHandicapIndex: checked ? false : prevState.competition.handicapRules.socialHandicapAsHandicapIndex,
          socialHandicapAsCourseHandicap: checked ? false : prevState.competition.handicapRules.socialHandicapAsCourseHandicap,
          socialHandicapAsPlayingHandicap: checked,
        },
      },
      err_socialHandicap: false,
    }));
  };

  _handleWhsAsHIChange = () => {
    this.setState(prevState => ({
      competition: {
        ...prevState.competition,
        handicapRules: {
          ...prevState.competition.handicapRules,
          whsFirstGameAsHandicapIndex: true,
          whsFirstGameAsCourseHandicap: false,
          whsFirstGameAsPlayingHandicap: false,
        },
      },
      err_whsHandicap: false,
    }));
  };

  _handleWhsAsCHChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    this.setState(prevState => ({
      competition: {
        ...prevState.competition,
        handicapRules: {
          ...prevState.competition.handicapRules,
          whsFirstGameAsHandicapIndex: !checked,
          whsFirstGameAsCourseHandicap: checked,
          whsFirstGameAsPlayingHandicap: false,
        },
      },
      err_whsHandicap: false,
    }));
  };

  _handleWhsAsPHChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    this.setState(prevState => ({
      competition: {
        ...prevState.competition,
        handicapRules: {
          ...prevState.competition.handicapRules,
          whsFirstGameAsHandicapIndex: !checked,
          whsFirstGameAsCourseHandicap: false,
          whsFirstGameAsPlayingHandicap: checked,
        },
      },
      err_whsHandicap: false,
    }));
  };

  renderActionButton() {
    const { classes } = this.props;
    const { loading } = this.props;
    if (loading)
      return (
        <div className={classes.loadingContainer}>
          <LinearProgress color="primary" variant={"indeterminate"} />
        </div>
      );
    else
      return (
        <div className={classes.actionButtonContainer}>
          <Button
            fullWidth
            className={classes.actionButton}
            variant={"contained"}
            color={"primary"}
            onClick={this._submitForm}
          >
            Save
          </Button>
          <Button
            fullWidth
            className={classes.actionButton}
            variant={"contained"}
            color={"secondary"}
            onClick={hideDialog}
          >
            Cancel
          </Button>
        </div>
      );
  }

  render() {
    const { classes } = this.props;
    const {
      name,
      type,
      introduction,
      eligibility,
      fullHandicapAllowance,
      guests,
      individual,
      scoringFormat,
      handicapRules,
      playingRules,
      holesNumber,
      expireRules,
      teamHandicapAllowance,
      handicapAdjustment,
      accessor,
    } = this.state.competition;
    const { err_name, err_intro, err_eligibility, err_expire } = this.state;
    const isTeamGame = playingRules.playingAs === PlayingAsFormats.Team;
    const isCombinedGame = isTeamGame && playingRules.playingFormat === PlayingFormats.Combined;
    const isPrivate = type === GameRules.CUSTOM;
    const editComp = this.props.edit;
    const ownerRef = editComp?.owner as UserBrifObj | undefined;
    const ownerId = ownerRef?._id ?? (editComp as { owner?: string } | undefined)?.owner;
    const isRuleOwner = ownerId != null && String(ownerId) === String(this.props.user._id);
    const canSetRuleOwner = !!editComp && isPrivate && (isRuleOwner || this.props.user.rule === Rules.Admin);
    const pendingOwner = (accessor || []).find(a => a && String(a._id) === String(this.state.pendingOwnerId));
    const ownerLabel = pendingOwner
      ? `${pendingOwner.name} ${pendingOwner.lastName}`
      : ownerRef
        ? `${ownerRef.name} ${ownerRef.lastName}`
        : "You (after save)";

    return (
      <Paper className={classes.paper}>
        <Typography variant={"h5"} className={classes.title}>
          Game Rule Setup
        </Typography>
        <div>
          {this.props.user.rule === Rules.Admin ? (
            <Selector options={this.ruleTypes} value={type} onChange={this._handleRuleTypeChange} />
          ) : null}
          <TextInput
            label={"Name"}
            margin={"dense"}
            variant={"outlined"}
            value={name}
            error={err_name}
            captalize
            name={"name"}
            onChange={this.handleTextInputChange}
            fullWidth
          />
          <TextInput
            label={"Information"}
            margin={"dense"}
            variant={"outlined"}
            value={introduction}
            error={err_intro}
            name={"introduction"}
            onChange={this.handleTextInputChange}
            rows={5}
            multiline
            fullWidth
          />
        </div>

        {/* Social Handicap Section */}
        <BorderedBox title={"Social Handicap Options"} variant={"h6"}>
          <div className={classes.inline}>
            <Typography variant="subtitle1">Allow players with a Social Handicap to play?</Typography>
            <Switch
              checked={!!handicapRules.allowPlayerWithSocialHandicap}
              onChange={this._handleAllowPlayerWithSocialHandicapChange}
            />
          </div>

          <div style={{ marginLeft: 16 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!handicapRules.socialHandicapAsHandicapIndex}
                  onChange={this._handleSocialHandicapAsHIChange}
                  disabled={!handicapRules.allowPlayerWithSocialHandicap}
                />
              }
              label="Treat as Handicap Index (HI)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!handicapRules.socialHandicapAsCourseHandicap}
                  onChange={this._handleSocialHandicapAsCHChange}
                  disabled={!handicapRules.allowPlayerWithSocialHandicap}
                />
              }
              label="Treat as Course Handicap (CH)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!handicapRules.socialHandicapAsPlayingHandicap}
                  onChange={this._handleSocialHandicapAsPHChange}
                  disabled={!handicapRules.allowPlayerWithSocialHandicap}
                />
              }
              label="Treat as Playing Handicap (PH)"
            />
            {this.state.err_socialHandicap && (
              <Typography color="error" variant="body2" style={{ marginLeft: 16 }}>
                Please select exactly one option for social handicap usage.
              </Typography>
            )}
          </div>
        </BorderedBox>

        <BorderedBox title={"WHS Handicap Options"} variant={"h6"}>
          <div style={{ marginLeft: 16 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!handicapRules.whsFirstGameAsHandicapIndex}
                  onChange={this._handleWhsAsHIChange}
                />
              }
              label="Treat as Handicap Index (HI)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!handicapRules.whsFirstGameAsCourseHandicap}
                  onChange={this._handleWhsAsCHChange}
                />
              }
              label="Treat as Course Handicap (CH)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!handicapRules.whsFirstGameAsPlayingHandicap}
                  onChange={this._handleWhsAsPHChange}
                />
              }
              label="Treat as Playing Handicap (PH)"
            />
            {this.state.err_whsHandicap && (
              <Typography color="error" variant="body2" style={{ marginLeft: 16 }}>
                Please select exactly one WHS handicap option.
              </Typography>
            )}
          </div>
        </BorderedBox>

        {/* Rest of Form */}
        <BorderedBox title={"Scoring Format"} variant={"h6"}>
          <div className={classes.inline}>
            <FormControl className={classes.inline} margin={"dense"}>
              <Select id="scoring-format" value={scoringFormat} onChange={this._onScoringFormatChanges}>
                <MenuItem value={Config.SCORING_FORMAT.STABLEFORD}>Stableford</MenuItem>
                <MenuItem value={Config.SCORING_FORMAT.STROKE}>Stroke</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <Select id="individual" value={individual} onChange={this._onIndividualChanges}>
                <MenuItem value={2}>2 Ball</MenuItem>
                <MenuItem value={3}>3 Ball</MenuItem>
                <MenuItem value={4}>4 Ball</MenuItem>
              </Select>
            </FormControl>
          </div>
        </BorderedBox>
        <BorderedBox title={"Competition Eligability"} variant={"h6"} error={err_eligibility}>
          <CompetitionEligability
            eligibility={eligibility}
            className={classes.smallInput}
            onEligibilityChanged={this.handleEligibilityChanged}
          />
        </BorderedBox>
        <BorderedBox title={"Number of Holes"} variant={"h6"}>
          <HolesNumberPicker
            className={classes.holesNumberContainer}
            onHoleNumberChanged={this.onChangeHoleNumbers}
            holesNumber={holesNumber}
          />
        </BorderedBox>
        <PlayingRules
          playingRules={playingRules}
          holesNumber={holesNumber}
          onPlayingRulesChanged={this.handlePlayingRulesChanged}
        />
        <HandicapRules
          handicapRules={handicapRules}
          scoringFormat={scoringFormat}
          onHandicapRulesChanged={this.handleHandicapRulesChanged}
        />
        {isPrivate || holesNumber === 9 ? (
          <>
            <HandicapsExpireRules
              expireRules={expireRules}
              onHandicapExpireRulesChanged={this.handleHandicapExpireRulesChanged}
              error={!!err_expire}
            />
            <HandicapAdjustmentSection
              handicapAdjustment={handicapAdjustment}
              onHandicapAdjustmentSelectChanged={this.handleHandicapAdjustmentSelectChanges}
              error={false}
              holesNumber={holesNumber}
            />
          </>
        ) : null}
        <HandicapAllowance
          teamHandicapAllowance={teamHandicapAllowance}
          fullHandicapAllowance={fullHandicapAllowance}
          isCombinedGame={isCombinedGame}
          isMultiRules={playingRules.multiRules === true}
          teamPlayers={playingRules.teamPlayers}
          minTeeShots={Array.isArray(playingRules.minimumTeeShots) ? playingRules.minimumTeeShots : []}
          onHandicapAllowanceChanged={this.handleHandicapAllowanceChanged}
        />
        <BorderedBox title={""} variant={"h6"}>
          <div className={classes.inline}>
            <Typography variant={"subtitle1"}>Allow scorers to add guests</Typography>
            <Switch value={guests} checked={guests} onChange={this.handleGuestSwitchChanged} />
          </div>
        </BorderedBox>
        {isPrivate && (
          <BorderedBox title={"Access and ownership"} variant={"h6"}>
            <Typography variant="body2" color="textSecondary" component="p" style={{ marginBottom: 6 }}>
              Everyone listed can use this rule in their games, edit it with the owner, and make their own copy with a
              new name. The owner is one of these people, set only with the switch.
            </Typography>
            <Typography variant="body2" color="textSecondary" component="p" style={{ marginBottom: 10 }}>
              Owner: <strong>{ownerLabel}</strong>
              {editComp?._id ? ` · On file: ${(accessor || []).length} organiser(s)` : null}
            </Typography>
            <OrganisersTable
              insideHandler={true}
              organisers={accessor || []}
              onChange={this._handleAccessorChange}
              ruleSharing={{
                showOwnerColumn: !!editComp?._id,
                ownerId: this.state.pendingOwnerId,
                onOwnerIdChange: this._handlePendingOwnerIdChange,
                canEditOwner: canSetRuleOwner,
                tableFrame: true,
              }}
            />
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {!editComp?._id ? (
                <Typography variant="body2" color="textSecondary">
                  Save this rule to enable the owner switch. You remain the owner when the rule is first created.
                </Typography>
              ) : !canSetRuleOwner ? (
                <Typography variant="body2" color="textSecondary">
                  Only the current owner (or an admin) can change who owns this rule. You can still change the list if
                  you may edit the rule.
                </Typography>
              ) : null}
              {this.state.shareError ? (
                <Typography variant="caption" color="error" component="p">
                  {this.state.shareError}
                </Typography>
              ) : null}
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 4 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this._onApplySharingClick}
                  disabled={
                    !editComp?._id ||
                    !this._isShareSectionDirty() ||
                    !!this.state.shareApplyLoading
                  }
                >
                  {this.state.shareApplyLoading ? "Applying" : "Apply changes"}
                </Button>
                {this._isShareSectionDirty() && editComp?._id ? (
                  <Typography variant="caption" color="textSecondary" component="span" style={{ maxWidth: 360 }}>
                    Unsaved list or owner. Use Apply, or save the whole form, which will apply this section as well.
                  </Typography>
                ) : null}
              </div>
            </div>
          </BorderedBox>
        )}
        {this.renderActionButton()}
      </Paper>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    title: {
      fontWeight: "bold",
    },
    paper: {
      display: "block",
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
      width: "auto",
      maxWidth: 500,
      minWidth: 360,
      backgroundColor: "rgba(255,255,255,0.85)",
      padding: theme.spacing(2),
    },
    inline: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    marginRight: {
      marginRight: theme.spacing(1),
    },
    smallInput: {
      width: 48,
    },
    actionButton: {
      marginTop: theme.spacing(1),
    },
    loadingContainer: {
      width: "100%",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    actionButtonContainer: {
      display: "flex",
    },
    holesNumberContainer: {
      textAlign: "center",
    },
  });

const mapStateToProps = (state: GlobalState) => {
  const { user } = state.auth;
  const { loading } = state.general;
  const { competitions } = state.competitions;
  return {
    user,
    loading,
    competitions: competitions ? Object.values(competitions) : [],
  };
};

export default connect(mapStateToProps, {
  insertCompetition: CompetitionActions.insert,
  updateCompetition: CompetitionActions.update,
  fetchCompetitionsList: CompetitionActions.fetch,
})(withStyles(styles)(CompetitionSetup));
